/* eslint-disable no-console */
/* eslint-disable max-len */
import { useRouteError } from 'react-router-dom';
import { ErrorType, ErrorPages } from '@components/ui/ErrorPages';
import { useAuthStore } from '@stores/authStore';
import { ALLOWED_ROLES } from '@constants';
import { CoreBox } from '@youscience/core';
import { FC, useEffect } from 'react';
import { CircularProgress, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  getChunkErrorExpiration,
  setChunkErrorExpiration,
  removeChunkErrorExpiration,
} from '@local-storage/loadingChunkFailure';
import { RouteError } from '@interfaces/routeError';

const MAX_RETRIES = 3;
const RETRY_DELAY = 10000;

const dynamicImportErrorRegex =
  /Loading chunk \d+ failed|Failed to fetch dynamically imported module|Failed to load resource|error loading dynamically imported module|Importing a module script failed/;

const ErrorPage: FC<{ sx?: SxProps<Theme> }> = ({ sx }) => {
  const error = useRouteError() as RouteError;

  const authSession = useAuthStore((state) => state.authSession);
  const { role = '' } = authSession.currentAccess ?? {};

  let retryingImportChunk = false;

  useEffect(() => {
    if (error?.message) {
      console.log('dynamicImportErrorRegex', JSON.stringify(dynamicImportErrorRegex.test(error.message)));

      if (dynamicImportErrorRegex.test(error.message)) {
        const chunkErrorData = getChunkErrorExpiration('chunk_failed');

        retryingImportChunk = true;

        if (chunkErrorData === null) {
          setChunkErrorExpiration('chunk_failed', JSON.stringify({ retryCount: 1 }), RETRY_DELAY);
          window.location.reload();
        } else {
          try {
            const parsedChunkErrorData = JSON.parse(chunkErrorData) as { retryCount: number };
            const { retryCount = 1 } = parsedChunkErrorData;

            if (retryCount < MAX_RETRIES) {
              setChunkErrorExpiration('chunk_failed', JSON.stringify({ retryCount: retryCount + 1 }), RETRY_DELAY);
              retryingImportChunk = true;

              window.location.reload();
            } else {
              retryingImportChunk = false;
              console.log(`Max retries (${MAX_RETRIES}) reached. Please try again later or contact support.`);
            }
          } catch (e) {
            retryingImportChunk = false;
            console.error('Error parsing chunk error data:', e);
          }
        }
      }
    }

    return () => {
      removeChunkErrorExpiration('chunk_failed');
      retryingImportChunk = false;
    };
  }, [error]);

  let errorCode: ErrorType = error?.status as ErrorType;

  if (!errorCode) {
    if (role && !ALLOWED_ROLES.includes(role)) {
      errorCode = 401;
    } else {
      errorCode = 'unknown';
    }
  }
  console.log('errorCode', errorCode);

  return (
    <CoreBox sx={{ height: '100%', ...sx }} id='error-page'>
      {retryingImportChunk ? (
        <CoreBox display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress size={60} />
        </CoreBox>
      ) : (
        <ErrorPages errorType={errorCode} homePageLink='/users' />
      )}
    </CoreBox>
  );
};

export default ErrorPage;
